import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import i18n from '../i18n';
import {
    LOCALE_STORAGE_KEY,
    SUPPORTED_LANGUAGES,
    normalizeLocale,
    getAvailableLanguages,
} from '../i18n/languages';
import { changeLanguage as applyLanguage } from '../utils/changeLanguage';
import { isRussianDomain } from '../config/domainLanguages';
import { getApiBaseUrl } from '../apiBase';

const LANG_CONFIRMED_KEY = 'sp_locale_confirmed';
const USER_REGION_KEY = 'user_region';

// ПОЛИТИКА ЯЗЫКОВ SportProgress — язык ≠ страна, регион скрыт от UI

function getBannerText(code, lang) {
    const name = lang?.nativeLabel || lang?.label || code;
    const texts = {
        ru: `Язык: ${name}`,
        en: `Language: ${name}`,
        hy: `Լեզուն: ${name}`,
        ka: `ენა: ${name}`,
        tr: `Dil: ${name}`,
        de: `Sprache: ${name}`,
        fr: `Langue: ${name}`,
        es: `Idioma: ${name}`,
        ar: `اللغة: ${name}`,
        az: `Dil: ${name}`,
        pl: `Język: ${name}`,
        kk: `Тіл: ${name}`,
        uz: `Til: ${name}`,
        cs: `Jazyk: ${name}`,
        'pt-BR': `Idioma: ${name}`,
    };
    return texts[code] || `Language: ${name}`;
}

export function useLanguageDetection() {
    const [bannerLang, setBannerLang] = useState(null);
    const [showBanner, setShowBanner] = useState(false);
    const [showPicker, setShowPicker] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
            const confirmed = localStorage.getItem(LANG_CONFIRMED_KEY);

            if (!isRussianDomain() && localStorage.getItem('i18n_user_chosen') !== 'true') {
                return;
            }

            if (saved && confirmed === 'true') {
                await applyLanguage(saved);
                return;
            }

            const browserLang = normalizeLocale(
                navigator.language?.split('-')[0] || 'en',
            );
            const supported = SUPPORTED_LANGUAGES.find((l) => l.code === browserLang);
            const detectedLang = supported?.code || 'en';
            const langObj = SUPPORTED_LANGUAGES.find((l) => l.code === detectedLang)
                || SUPPORTED_LANGUAGES[0];

            await applyLanguage(detectedLang);

            try {
                const { data } = await axios.get(`${getApiBaseUrl()}/geo/detect`, { timeout: 3000 });
                if (data?.region) {
                    localStorage.setItem(USER_REGION_KEY, data.region);
                }
            } catch {
                /* not critical */
            }

            if (!cancelled) {
                setBannerLang({ ...langObj, bannerText: getBannerText(detectedLang, langObj) });
                setShowBanner(true);
            }
        };

        init();
        return () => { cancelled = true; };
    }, []);

    const confirmLanguage = useCallback(() => {
        const code = normalizeLocale(i18n.language);
        localStorage.setItem(LOCALE_STORAGE_KEY, code);
        localStorage.setItem(LANG_CONFIRMED_KEY, 'true');
        void applyLanguage(code);
        setShowBanner(false);
        setShowPicker(false);
    }, []);

    const changeLanguage = useCallback(async (code) => {
        await applyLanguage(code);
        const normalized = normalizeLocale(code);
        const langObj = SUPPORTED_LANGUAGES.find((l) => l.code === normalized);
        setBannerLang({
            ...langObj,
            bannerText: getBannerText(normalized, langObj),
        });
        setShowPicker(false);
    }, []);

    return {
        showBanner,
        showPicker,
        bannerLang,
        setShowPicker,
        confirmLanguage,
        changeLanguage,
        languages: getAvailableLanguages(),
    };
}
